/**
 * NRRMaturityView — Radar chart for 6 NRR maturity dimensions
 * with expandable sub-theme detail for the top 3 most impactful dimensions.
 */

import { useState } from "react";
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { ChevronDown, ChevronUp } from "lucide-react";
import type { NrrMaturityDimension, MaturityLevel } from "../types/diagnostic";

interface NRRMaturityViewProps {
  maturity: NrrMaturityDimension[];
  clientName: string;
}

/** Maps maturity level to a numeric score for the radar chart. */
function levelToScore(level: MaturityLevel): number {
  switch (level) {
    case "basic":
      return 1;
    case "advanced":
      return 2;
    case "next-gen":
      return 3;
  }
}

/** Returns display label and colour for a maturity level. */
function levelMeta(level: MaturityLevel) {
  switch (level) {
    case "basic":
      return { label: "Basic", color: "text-red-600", bg: "bg-red-100", border: "border-red-300", dot: "bg-red-500" };
    case "advanced":
      return { label: "Advanced", color: "text-amber-600", bg: "bg-amber-100", border: "border-amber-300", dot: "bg-amber-500" };
    case "next-gen":
      return { label: "Next-gen", color: "text-green-600", bg: "bg-green-100", border: "border-green-300", dot: "bg-green-500" };
  }
}

export function NRRMaturityView({ maturity, clientName }: NRRMaturityViewProps) {
  const [expandedDim, setExpandedDim] = useState<string | null>(null);

  /** Data for the radar chart. */
  const radarData = maturity.map((dim) => ({
    dimension: dim.label.length > 20 ? dim.label.slice(0, 20) + "..." : dim.label,
    fullLabel: dim.label,
    current: levelToScore(dim.currentLevel),
    target: levelToScore(dim.targetLevel),
  }));

  return (
    <div className="mck-card p-6 space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-lg font-semibold text-text-primary">
          NRR Maturity Assessment
        </h2>
        <p className="text-sm text-text-secondary mt-1">
          {clientName}&apos;s maturity across the 6 dimensions that drive best-in-class NRR performance (McKinsey NRR Benchmark, N=101)
        </p>
      </div>

      {/* Radar + Dimension List side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Radar Chart */}
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart data={radarData} outerRadius="70%">
              <PolarGrid stroke="#e5e5e5" />
              <PolarAngleAxis
                dataKey="dimension"
                tick={{ fontSize: 10, fill: "#4a4a4a" }}
                tickLine={false}
              />
              <PolarRadiusAxis
                angle={90}
                domain={[0, 3]}
                tick={{ fontSize: 9, fill: "#9ca3af" }}
                tickCount={4}
                tickFormatter={(v: number) => {
                  if (v === 1) return "Basic";
                  if (v === 2) return "Adv.";
                  if (v === 3) return "Next-gen";
                  return "";
                }}
              />
              <Radar
                name="Current"
                dataKey="current"
                stroke="#dc2626"
                fill="#dc2626"
                fillOpacity={0.15}
                strokeWidth={2}
              />
              <Radar
                name="Target (2-3yr)"
                dataKey="target"
                stroke="#16a34a"
                fill="#16a34a"
                fillOpacity={0.08}
                strokeWidth={2}
                strokeDasharray="6 3"
              />
              <Legend
                wrapperStyle={{ fontSize: 11 }}
                iconType="line"
              />
            </RadarChart>
          </ResponsiveContainer>
        </div>

        {/* Dimension Summary List */}
        <div className="space-y-2">
          {maturity.map((dim) => {
            const curr = levelMeta(dim.currentLevel);
            const tgt = levelMeta(dim.targetLevel);
            const hasThemes = dim.themes && dim.themes.length > 0;
            const isExpanded = expandedDim === dim.id;

            return (
              <div key={dim.id} className="border border-surface-borderLight rounded-lg">
                <button
                  className="w-full flex items-center justify-between p-3 text-left hover:bg-surface-secondary transition-colors rounded-lg"
                  onClick={() => hasThemes && setExpandedDim(isExpanded ? null : dim.id)}
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${curr.dot}`} />
                    <span className="text-sm font-medium text-text-primary truncate">
                      {dim.label}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${curr.bg} ${curr.color} font-medium`}>
                      {curr.label}
                    </span>
                    <span className="text-xs text-text-tertiary">→</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${tgt.bg} ${tgt.color} font-medium`}>
                      {tgt.label}
                    </span>
                    {hasThemes && (
                      isExpanded ? <ChevronUp className="w-4 h-4 text-text-tertiary" /> : <ChevronDown className="w-4 h-4 text-text-tertiary" />
                    )}
                  </div>
                </button>

                {/* Expanded Sub-themes */}
                {isExpanded && dim.themes && (
                  <div className="px-3 pb-3 space-y-2 animate-fade-in">
                    <p className="text-xs text-text-secondary italic px-1">{dim.rationale}</p>
                    {dim.themes.map((theme) => {
                      const tc = levelMeta(theme.currentLevel);
                      const tt = levelMeta(theme.targetLevel);
                      return (
                        <div
                          key={theme.id}
                          className="flex items-start gap-3 bg-surface-secondary rounded-md p-2.5 ml-5"
                        >
                          <span className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${tc.dot}`} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-xs font-medium text-text-primary">
                                {theme.label}
                              </span>
                              <div className="flex items-center gap-1.5 flex-shrink-0">
                                <span className={`text-[10px] px-1.5 py-0.5 rounded ${tc.bg} ${tc.color}`}>
                                  {tc.label}
                                </span>
                                <span className="text-[10px] text-text-tertiary">→</span>
                                <span className={`text-[10px] px-1.5 py-0.5 rounded ${tt.bg} ${tt.color}`}>
                                  {tt.label}
                                </span>
                              </div>
                            </div>
                            <p className="text-[11px] text-text-tertiary mt-0.5 leading-relaxed">
                              {theme.note}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend for maturity levels */}
      <div className="flex items-center gap-6 text-xs text-text-secondary border-t border-surface-borderLight pt-4">
        <span className="font-medium text-text-primary">Maturity scale:</span>
        {(["basic", "advanced", "next-gen"] as MaturityLevel[]).map((level) => {
          const m = levelMeta(level);
          return (
            <span key={level} className="flex items-center gap-1.5">
              <span className={`w-2.5 h-2.5 rounded-full ${m.dot}`} />
              {m.label}
            </span>
          );
        })}
        <span className="ml-auto text-text-tertiary italic">
          Source: McKinsey NRR Maturity Benchmark (N=101 B2B SaaS companies)
        </span>
      </div>
    </div>
  );
}
